import React, { useState } from 'react';
import { useProjects, useDeleteProject, useChangeProjectStatus } from '../hooks/useProjects';
import { useExpenses } from '../hooks/useExpenses';
import { useInvoices } from '../hooks/useInvoices';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '../hooks/usePermissions';
import { ProtectedElement } from '../components/common/ProtectedElement';
import { ConfirmModal } from '../components/common/ConfirmModal';

interface ConfirmState {
  isOpen: boolean;
  title: string;
  message: string;
  confirmText: string;
  confirmColor: 'blue' | 'red' | 'green' | 'yellow';
  onConfirm: () => void;
}

const Projects: React.FC = () => {
  const navigate = useNavigate();
  const { canEditProject } = usePermissions();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'active' | 'completed' | 'suspended' | 'all'>('active');
  const [dueDateFilter, setDueDateFilter] = useState<'all' | 'overdue' | 'due_soon' | 'on_time'>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [changingStatusId, setChangingStatusId] = useState<string | null>(null);
  const ITEMS_PER_PAGE = 12;
  const [confirmState, setConfirmState] = useState<ConfirmState>({
    isOpen: false,
    title: '',
    message: '',
    confirmText: 'Confirmar',
    confirmColor: 'blue',
    onConfirm: () => {},
  });
  const [deleteInfo, setDeleteInfo] = useState<{
    projectName: string;
    expenseCount: number;
    expenseTotal: number;
    invoiceCount: number;
    invoiceTotal: number;
    pendingInvoiceCount: number;
  } | null>(null);
  
  const { data: projects, isLoading, error } = useProjects(statusFilter);
  const { data: allExpenses } = useExpenses();
  const { data: allInvoices } = useInvoices();
  const deleteProject = useDeleteProject();
  const changeStatus = useChangeProjectStatus();

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
    }).format(amount);
  };

  const closeModal = () => {
    setConfirmState(prev => ({ ...prev, isOpen: false }));
  };

  const handleDelete = async (e: React.MouseEvent, projectId: string, projectName: string) => {
    e.stopPropagation();
    
    // Count associated expenses and invoices
    const projectExpenses = allExpenses?.items?.filter(exp => exp.projectId === projectId) || [];
    const projectInvoices = allInvoices?.items?.filter(inv => inv.projectId === projectId) || [];
    const totalExpenses = projectExpenses.reduce((sum, exp) => sum + exp.amount, 0);
    const totalInvoices = projectInvoices.reduce((sum, inv) => sum + inv.totalAmount, 0);
    const pendingInvoices = projectInvoices.filter(inv => inv.status === 'pending');
    
    setDeleteInfo({
      projectName,
      expenseCount: projectExpenses.length,
      expenseTotal: totalExpenses,
      invoiceCount: projectInvoices.length,
      invoiceTotal: totalInvoices,
      pendingInvoiceCount: pendingInvoices.length,
    });
    
    setConfirmState({
      isOpen: true,
      title: 'Eliminar Proyecto',
      message: '',
      confirmText: 'Eliminar Proyecto',
      confirmColor: 'red',
      onConfirm: async () => {
        closeModal();
        setDeleteInfo(null);
        setDeletingId(projectId);
        try {
          await deleteProject.mutateAsync(projectId);
        } finally {
          setDeletingId(null);
        }
      },
    });
  };

  const handleChangeStatus = async (e: React.MouseEvent, projectId: string, newStatus: 'active' | 'completed' | 'suspended') => {
    e.stopPropagation();
    
    const statusLabels = {
      active: 'Activo',
      completed: 'Finalizado',
      suspended: 'Detenido'
    };

    const statusColors: Record<string, 'blue' | 'red' | 'green' | 'yellow'> = {
      active: 'green',
      completed: 'blue',
      suspended: 'yellow'
    };
    
    setConfirmState({
      isOpen: true,
      title: 'Cambiar Estado del Proyecto',
      message: `¿Deseas cambiar el estado del proyecto a "${statusLabels[newStatus]}"?`,
      confirmText: 'Cambiar Estado',
      confirmColor: statusColors[newStatus],
      onConfirm: async () => {
        closeModal();
        setChangingStatusId(projectId);
        try {
          await changeStatus.mutateAsync({ projectId, status: newStatus });
        } finally {
          setChangingStatusId(null);
        }
      },
    });
  };

  const handleEdit = (e: React.MouseEvent, projectId: string) => {
    e.stopPropagation();
    navigate(`/projects/${projectId}/edit`);
  };

  const filteredProjects = projects?.filter(project => {
    // Text search
    const matchesSearch = project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      project.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      project.clientName?.toLowerCase().includes(searchTerm.toLowerCase());
    if (!matchesSearch) return false;

    // Due date filter
    if (dueDateFilter !== 'all' && project.dueDate) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const due = new Date(project.dueDate + 'T00:00:00');
      const diffDays = Math.ceil((due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

      if (dueDateFilter === 'overdue' && diffDays >= 0) return false;
      if (dueDateFilter === 'due_soon' && (diffDays < 0 || diffDays > 30)) return false;
      if (dueDateFilter === 'on_time' && diffDays <= 30) return false;
    } else if (dueDateFilter !== 'all' && !project.dueDate) {
      // Projects without due date: show in 'on_time', hide in 'overdue' and 'due_soon'
      if (dueDateFilter === 'overdue' || dueDateFilter === 'due_soon') return false;
    }

    return true;
  }) || [];

  // Pagination
  const totalPages = Math.ceil(filteredProjects.length / ITEMS_PER_PAGE);
  const paginatedProjects = filteredProjects.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  // Reset page when filters change
  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };
  const handleStatusFilterChange = (value: 'active' | 'completed' | 'suspended' | 'all') => {
    setStatusFilter(value);
    setCurrentPage(1);
  };
  const handleDueDateFilterChange = (value: 'all' | 'overdue' | 'due_soon' | 'on_time') => {
    setDueDateFilter(value);
    setCurrentPage(1);
  };

  const getBudgetStatusColor = (status: string) => {
    switch (status) {
      case 'under':
        return 'bg-green-100 text-green-800';
      case 'over':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'under':
        return 'En Presupuesto';
      case 'over':
        return 'Excedido';
      default:
        return status;
    }
  };

  const getProjectStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">Activo</span>;
      case 'completed':
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">Finalizado</span>;
      case 'suspended':
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">Detenido</span>;
      default:
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">{status}</span>;
    }
  };

  const calculatePercentage = (spent: number, budget: number) => {
    if (budget === 0) return 0;
    return Math.min((spent / budget) * 100, 100);
  };

  /**
   * Returns deadline status info based on days remaining.
   * - Verde: más de 30 días
   * - Amarillo: entre 7 y 30 días
   * - Rojo: menos de 7 días o vencido
   */
  const getDueDateStatus = (dueDate?: string) => {
    if (!dueDate) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(dueDate + 'T00:00:00');
    const diffMs = due.getTime() - today.getTime();
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays < 0) {
      return { color: 'bg-red-100 text-red-800', label: `Vencido hace ${Math.abs(diffDays)} día(s)`, dot: 'bg-red-500' };
    }
    if (diffDays === 0) {
      return { color: 'bg-red-100 text-red-800', label: 'Vence hoy', dot: 'bg-red-500' };
    }
    if (diffDays <= 7) {
      return { color: 'bg-red-100 text-red-800', label: `Vence en ${diffDays} día(s)`, dot: 'bg-red-500' };
    }
    if (diffDays <= 30) {
      return { color: 'bg-yellow-100 text-yellow-800', label: `Vence en ${diffDays} días`, dot: 'bg-yellow-500' };
    }
    return { color: 'bg-green-100 text-green-800', label: `Vence en ${diffDays} días`, dot: 'bg-green-500' };
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">Error al cargar proyectos: {String(error)}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmState.isOpen}
        title={confirmState.title}
        message={confirmState.message}
        confirmText={confirmState.confirmText}
        confirmColor={confirmState.confirmColor}
        confirmDisabled={deleteInfo ? deleteInfo.pendingInvoiceCount > 0 : false}
        onConfirm={confirmState.onConfirm}
        onCancel={() => { closeModal(); setDeleteInfo(null); }}
      >
        {deleteInfo && (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              El proyecto <span className="font-semibold text-gray-900">"{deleteInfo.projectName}"</span> y todos sus datos serán eliminados del sistema.
            </p>

            {(deleteInfo.expenseCount > 0 || deleteInfo.invoiceCount > 0) && (
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Se eliminarán también</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                      <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{deleteInfo.expenseCount}</p>
                      <p className="text-xs text-gray-500">Gastos ({formatCurrency(deleteInfo.expenseTotal)})</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{deleteInfo.invoiceCount}</p>
                      <p className="text-xs text-gray-500">Facturas ({formatCurrency(deleteInfo.invoiceTotal)})</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="flex items-start gap-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              <svg className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.962-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <span>
                {deleteInfo.pendingInvoiceCount > 0
                  ? `No se puede eliminar: tiene ${deleteInfo.pendingInvoiceCount} factura(s) pendiente(s) de pago. Márcalas como pagadas o anuladas primero.`
                  : 'Esta acción no se puede deshacer desde el portal.'
                }
              </span>
            </div>
          </div>
        )}
      </ConfirmModal>

      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">Proyectos</h1>
        
        {/* Create Project button - Admin and Superadmin only */}
        <ProtectedElement permission="canCreateProject">
          <button
            onClick={() => navigate('/projects/new')}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            + Nuevo Proyecto
          </button>
        </ProtectedElement>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 space-y-4">
        {/* Search bar */}
        <input
          type="text"
          placeholder="Buscar por nombre, descripción o cliente..."
          value={searchTerm}
          onChange={(e) => handleSearchChange(e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        
        {/* Status filter */}
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => handleStatusFilterChange('active')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              statusFilter === 'active'
                ? 'bg-green-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Activos
          </button>
          <button
            onClick={() => handleStatusFilterChange('completed')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              statusFilter === 'completed'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Finalizados
          </button>
          <button
            onClick={() => handleStatusFilterChange('suspended')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              statusFilter === 'suspended'
                ? 'bg-yellow-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Detenidos
          </button>
          <button
            onClick={() => handleStatusFilterChange('all')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              statusFilter === 'all'
                ? 'bg-gray-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Todos
          </button>
        </div>

        {/* Due date filter */}
        <div className="flex gap-2 flex-wrap items-center">
          <span className="text-sm text-gray-600 mr-2">Vencimiento:</span>
          <button
            onClick={() => handleDueDateFilterChange('all')}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
              dueDateFilter === 'all'
                ? 'bg-gray-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Todos
          </button>
          <button
            onClick={() => handleDueDateFilterChange('overdue')}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
              dueDateFilter === 'overdue'
                ? 'bg-red-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Vencidos
          </button>
          <button
            onClick={() => handleDueDateFilterChange('due_soon')}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
              dueDateFilter === 'due_soon'
                ? 'bg-yellow-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Por vencer (30 días)
          </button>
          <button
            onClick={() => handleDueDateFilterChange('on_time')}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
              dueDateFilter === 'on_time'
                ? 'bg-green-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Sin urgencia
          </button>
        </div>

        {/* Results count */}
        <p className="text-sm text-gray-500">
          {filteredProjects.length} proyecto{filteredProjects.length !== 1 ? 's' : ''} encontrado{filteredProjects.length !== 1 ? 's' : ''}
        </p>
      </div>

      {/* Projects grid */}
      {filteredProjects.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <p className="text-gray-600">
            {searchTerm ? 'No se encontraron proyectos' : 'No hay proyectos en esta categoría'}
          </p>
        </div>
      ) : (
        <>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {paginatedProjects.map((project) => {
            const percentage = calculatePercentage(project.spent, project.budget);
            const statusColor = project.budgetStatus === 'over' ? 'bg-red-600' : 'bg-green-600';
            const dueDateStatus = getDueDateStatus(project.dueDate);
            
            return (
              <div
                key={project.projectId}
                className="bg-white rounded-lg shadow overflow-hidden hover:shadow-lg transition-shadow group"
              >
                {/* Clickable area for navigation - All users can view project details */}
                <div 
                  onClick={() => navigate(`/projects/${project.projectId}`)}
                  className="cursor-pointer"
                >
                  <div className="p-6">
                    <div className="flex justify-between items-start mb-4">
                      <h3 className="text-xl font-semibold text-gray-900 flex-1">
                        {project.name}
                      </h3>
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-medium ${getBudgetStatusColor(
                          project.budgetStatus
                        )}`}
                      >
                        {getStatusText(project.budgetStatus)}
                      </span>
                    </div>

                    {project.clientName && (
                      <p className="text-sm text-gray-500 -mt-2 mb-4">
                        <span className="inline-flex items-center gap-1">
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                          {project.clientName}
                        </span>
                      </p>
                    )}

                    {project.description && (
                      <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                        {project.description}
                      </p>
                    )}

                    {/* Due date indicator */}
                    {dueDateStatus && (
                      <div className="mb-4">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${dueDateStatus.color}`}>
                          <span className={`w-2 h-2 rounded-full ${dueDateStatus.dot}`}></span>
                          {dueDateStatus.label}
                        </span>
                      </div>
                    )}

                    {/* Budget progress */}
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Gastado</span>
                        <span className="font-medium text-gray-900">
                          {formatCurrency(project.spent)}
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full transition-all ${statusColor}`}
                          style={{ width: `${percentage}%` }}
                        ></div>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Presupuesto</span>
                        <span className="font-medium text-gray-900">
                          {formatCurrency(project.budget)}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Disponible</span>
                        <span className={`font-medium ${project.available < 0 ? 'text-red-600' : 'text-green-600'}`}>
                          {formatCurrency(project.available)}
                        </span>
                      </div>
                    </div>

                    {/* Status */}
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <div className="flex justify-between items-center text-xs text-gray-500">
                        <div className="flex items-center gap-2">
                          <span>Estado:</span>
                          {getProjectStatusBadge(project.status)}
                        </div>
                        <div className="text-right">
                          {project.dueDate && (
                            <span className="block">Vence: {new Date(project.dueDate + 'T00:00:00').toLocaleDateString('es-CL')}</span>
                          )}
                          <span>Creado: {new Date(project.createdAt).toLocaleDateString('es-CL')}</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Read-only indicator for colaborador users */}
                    {!canEditProject && (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <p className="text-xs text-gray-500 italic">
                          Vista de solo lectura
                        </p>
                      </div>
                    )}
                  </div>
                </div>
                {/* End of clickable area */}

                {/* Admin action buttons - Hover overlay */}
                {canEditProject && (
                  <div className="px-6 pb-4 pt-2 border-t border-gray-100 bg-gray-50 opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="flex gap-2 items-center justify-between">
                      {/* Edit button */}
                      <button
                        onClick={(e) => handleEdit(e, project.projectId)}
                        className="flex items-center gap-1 px-3 py-1.5 text-xs text-blue-700 hover:bg-blue-100 rounded transition-colors"
                        title="Editar proyecto"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        Editar
                      </button>

                      {/* Status change buttons */}
                      <div className="flex gap-1">
                        {project.status !== 'active' && (
                          <button
                            onClick={(e) => handleChangeStatus(e, project.projectId, 'active')}
                            disabled={changingStatusId === project.projectId}
                            className="flex items-center gap-1 px-2 py-1.5 text-xs text-green-700 hover:bg-green-100 rounded transition-colors disabled:opacity-50"
                            title="Marcar como Activo"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            Activo
                          </button>
                        )}
                        
                        {project.status !== 'completed' && (
                          <button
                            onClick={(e) => handleChangeStatus(e, project.projectId, 'completed')}
                            disabled={changingStatusId === project.projectId}
                            className="flex items-center gap-1 px-2 py-1.5 text-xs text-blue-700 hover:bg-blue-100 rounded transition-colors disabled:opacity-50"
                            title="Marcar como Finalizado"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            Finalizado
                          </button>
                        )}
                        
                        {project.status !== 'suspended' && (
                          <button
                            onClick={(e) => handleChangeStatus(e, project.projectId, 'suspended')}
                            disabled={changingStatusId === project.projectId}
                            className="flex items-center gap-1 px-2 py-1.5 text-xs text-yellow-700 hover:bg-yellow-100 rounded transition-colors disabled:opacity-50"
                            title="Marcar como Detenido"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            Detenido
                          </button>
                        )}
                      </div>

                      {/* Delete button */}
                      <button
                        onClick={(e) => handleDelete(e, project.projectId, project.name)}
                        disabled={deletingId === project.projectId}
                        className="flex items-center gap-1 px-2 py-1.5 text-xs text-red-700 hover:bg-red-100 rounded transition-colors disabled:opacity-50"
                        title="Eliminar proyecto"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        {deletingId === project.projectId ? 'Eliminando...' : 'Eliminar'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-2 mt-6">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              ← Anterior
            </button>
            <span className="text-sm text-gray-600">
              Página {currentPage} de {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Siguiente →
            </button>
          </div>
        )}
      </>
      )}
    </div>
  );
};

export default Projects;
